// The `plan` block: what the agent intends to do, for work too long to sit behind
// a spinner.
//
// It is a statement of intent, not live state. The agent states the plan once and
// may restate it (a key is a slot, so a restatement replaces it in place), but
// nothing depends on that: progress shows through the jump pills that appear as
// each piece of work lands. A mechanism that needs the model to re-emit on a cadence
// is a mechanism that works most of the time, and "most of the time" is how a
// progress display becomes a lie.
//
// Parsing is deliberately forgiving. The body is JSON as text — rgip has a
// `content_json` column but the chat path never populates it — and a block whose
// JSON does not parse falls back to rendering as markdown, so a malformed plan is
// readable rather than absent.

/** Where a step has got to. Anything unrecognised reads as `pending`. */
export type PlanStepStatus = 'pending' | 'running' | 'done' | 'skipped';

export interface PlanStep {
  text: string;
  status: PlanStepStatus;
  /** The object this step is about, when it names one. Renders as a jump link. */
  tree: number | null;
  node: number | null;
}

export interface Plan {
  goal: string;
  steps: PlanStep[];
}

const STATUSES: PlanStepStatus[] = ['pending', 'running', 'done', 'skipped'];

function asStatus(raw: unknown): PlanStepStatus {
  return STATUSES.includes(raw as PlanStepStatus)
    ? (raw as PlanStepStatus)
    : 'pending';
}

/** A positive integer, or null. Tree and node ids are 1-based in Studio. */
function asId(raw: unknown): number | null {
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : null;
}

/**
 * Read a plan out of a block body, or null when it is not one.
 *
 * Null is the signal to fall back to markdown, so every rejection here has to be a
 * real one: a plan with no steps is not a plan, but a plan whose steps are missing
 * their status is fine and reads as pending.
 */
export function parsePlan(raw: string): Plan | null {
  let doc: unknown;
  try {
    doc = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof doc !== 'object' || doc === null) return null;
  const record = doc as Record<string, unknown>;
  const rawSteps = record.steps;
  if (!Array.isArray(rawSteps)) return null;

  const steps: PlanStep[] = [];
  for (const item of rawSteps) {
    // A step is its text. One without any is not a step, and rendering an empty row
    // would read as work the agent forgot to name.
    const text =
      typeof item === 'string'
        ? item
        : typeof (item as Record<string, unknown>)?.text === 'string'
        ? ((item as Record<string, unknown>).text as string)
        : '';
    if (!text.trim()) continue;
    const fields = (
      typeof item === 'object' && item !== null ? item : {}
    ) as Record<string, unknown>;
    steps.push({
      text: text.trim(),
      status: asStatus(fields.status),
      tree: asId(fields.tree),
      node: asId(fields.node),
    });
  }
  if (steps.length === 0) return null;

  return {
    goal: typeof record.goal === 'string' ? record.goal.trim() : '',
    steps,
  };
}

/**
 * How far along the plan is, for the header count.
 *
 * Skipped steps count as settled: a plan of five where one was skipped and four are
 * done is finished, and reporting 4/5 would leave it looking permanently stuck.
 */
export function planProgress(plan: Plan): { settled: number; total: number } {
  return {
    settled: plan.steps.filter(
      (s) => s.status === 'done' || s.status === 'skipped'
    ).length,
    total: plan.steps.length,
  };
}
